import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Save, ArrowLeft } from 'lucide-react';
import { useSession } from '../../hooks/useSession';
import { SessionProgress, StepContent } from './session';

export default function CounselingSession() {
  const {
    sessionData,
    loading,
    error,
    activeStep,
    isTerminating,
    handleFormSubmit,
    handleSaveAndContinue,
    handleCancel,
    handleTerminate,
    handleFormChange,
    hasUnsavedChanges,
    lastAutoSave,
    isStepComplete,
    isSessionReady,
    actualSessionId,
    successMessage,
    // Add these if they don't exist in your hook
    goToPreviousStep,
    canGoBack,
    setActiveStep // You'll need to expose this from your useSession hook
  } = useSession();

  // Determine if this is a first session or returning client
  const [isFirstSession, setIsFirstSession] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);

  const [showSafetyForm, setShowSafetyForm] = useState(false);
  const [showTerminationForm, setShowTerminationForm] = useState(false);

  // Add these handler functions
  const handleSafetyFormSubmit = (data) => {
    // Save safety form data
    handleFormSubmit("safety", data);
    setShowSafetyForm(false);
  };

  const handleTerminationFormSubmit = (data) => {
    // Save termination form data
    handleFormSubmit("termination", data);
    setShowTerminationForm(false);
  };

  const openSafetyForm = () => setShowSafetyForm(true);
  const openTerminationForm = () => setShowTerminationForm(true);

  // Effect to check session number when session data is loaded
  useEffect(() => {
    if (sessionData && sessionData.record?.sessionNumber) {
      setIsFirstSession(sessionData.record.sessionNumber === 1);
    }
  }, [sessionData]);

  // Define steps for first session and returning sessions
  const firstSessionSteps = ['consent', 'intake', 'mentalStatus'];
  const returningSessionSteps = ['record'];

  // Get appropriate steps based on session type
  const currentSteps = isFirstSession ? firstSessionSteps : returningSessionSteps;

  // Back button handler
  const handleBack = () => {
    if (showSafetyForm) {
      setShowSafetyForm(false);
      return;
    }

    if (showTerminationForm) {
      setShowTerminationForm(false);
      return;
    }

    // For main session steps, use the hook's navigation method
    goToPreviousStep();
  };

  // Check if back button should be shown
  const canShowBackButton = () => {
    if (showSafetyForm || showTerminationForm) {
      return true; // Can always go back from additional forms
    }

    // For main steps, use hook's canGoBack method
    return canGoBack();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isSessionReady) {
    return <div>Loading session data...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">
          {isFirstSession ? 'Initial Counseling Session' : 'Follow-up Counseling Session'}
        </h1>
        <div className="flex items-center gap-2">
          <div className="bg-blue-50 px-3 py-1 rounded text-blue-700">
            Session #{sessionData?.record?.sessionNumber || '?'}
          </div>
          {/* Auto-save status indicator */}
          {hasUnsavedChanges && (
            <div className="bg-amber-50 px-3 py-1 rounded text-amber-700 flex items-center gap-1">
              <Save className="h-4 w-4 animate-pulse" />
              <span>Auto-saving...</span>
            </div>
          )}

          {lastAutoSave && !hasUnsavedChanges && (
            <div className="bg-green-50 px-3 py-1 rounded text-green-700 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              <span>Saved {new Date(lastAutoSave).toLocaleTimeString()}</span>
            </div>
          )}

          {sessionData?.status === "completed" && (
            <div className="bg-green-50 px-3 py-1 rounded text-green-700 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              <span>Completed</span>
            </div>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <SessionProgress
        activeStep={activeStep}
        sessionData={sessionData}
        isStepComplete={isStepComplete}
        isTerminating={isTerminating}
        steps={currentSteps}
        onOpenSafetyForm={openSafetyForm}
        onOpenTerminationForm={openTerminationForm}
        // Pass draft-related props to StepContent
        handleFormChange={handleFormChange}
        hasUnsavedChanges={hasUnsavedChanges}
        lastAutoSave={lastAutoSave}
      />

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-2">
        <StepContent
          activeStep={activeStep}
          sessionData={sessionData}
          onSubmit={handleFormSubmit}
          isNewClient={isFirstSession}
          validSteps={currentSteps}
          actualSessionId={actualSessionId}
          showSafetyForm={showSafetyForm}
          showTerminationForm={showTerminationForm}
          onSafetySubmit={handleSafetyFormSubmit}
          onTerminationSubmit={handleTerminationFormSubmit}
          // Add navigation props
          onBack={handleBack}
          canGoBack={canShowBackButton}
        />

        <div className="px-6 py-4 border-t flex justify-between">
          {/* Left side - Back button */}
          <div>
            {canShowBackButton() && (
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                disabled={sessionData?.status === "completed"}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            )}
          </div>

          {/* Right side - Action buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              disabled={sessionData?.status === "completed"}
            >
              Cancel
            </button>

            {!isTerminating && sessionData?.status !== "completed" && !showSafetyForm && !showTerminationForm && (
              <button
                onClick={handleSaveAndContinue}
                disabled={!isStepComplete(activeStep)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
              >
                {activeStep === "mentalStatus" ? "Complete Session" : "Save & Continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// import { useState, useEffect } from 'react';
// import { AlertCircle, CheckCircle, Save } from 'lucide-react';
// import { useSession } from '../../hooks/useSession';
// import { SessionProgress, StepContent } from './session';

// export default function CounselingSession() {
//   const {
//     sessionData,
//     loading,
//     error,
//     activeStep,
//     isTerminating,
//     handleFormSubmit,
//     handleSaveAndContinue,
//     handleCancel,
//     handleTerminate,
//     handleFormChange,
//     hasUnsavedChanges,
//     lastAutoSave,
//     isStepComplete,
//     isSessionReady,
//     actualSessionId,
//     successMessage
//   } = useSession();

//   // Determine if this is a first session or returning client
//   const [isFirstSession, setIsFirstSession] = useState(true);
//   const [isCompleting, setIsCompleting] = useState(false);

//   const [showSafetyForm, setShowSafetyForm] = useState(false);
//   const [showTerminationForm, setShowTerminationForm] = useState(false);

//   // Add these handler functions
//   const handleSafetyFormSubmit = (data) => {
//     // Save safety form data
//     handleFormSubmit("safety", data);
//     setShowSafetyForm(false);
//   };

//   const handleTerminationFormSubmit = (data) => {
//     // Save termination form data
//     handleFormSubmit("termination", data);
//     setShowTerminationForm(false);
//   };

//   const openSafetyForm = () => setShowSafetyForm(true);
//   const openTerminationForm = () => setShowTerminationForm(true);

//   // Effect to check session number when session data is loaded
//   useEffect(() => {
//     if (sessionData && sessionData.record.sessionNumber) {
//       setIsFirstSession(sessionData.record.sessionNumber === 1);
//     }
//   }, [sessionData]);

//   // Define steps for first session and returning sessions
//   const firstSessionSteps = ['consent', 'intake', 'mentalStatus'];
//   const returningSessionSteps = ['record'];

//   // Get appropriate steps based on session type
//   const currentSteps = isFirstSession ? firstSessionSteps : returningSessionSteps;

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center min-h-screen">
//         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
//       </div>
//     );
//   }

//   if (!isSessionReady) {
//     return <div>Loading session data...</div>;
//   }

//   return (
//     <div className="max-w-4xl mx-auto">
//       <div className="mb-6 flex justify-between items-center">
//         <h1 className="text-2xl font-bold text-gray-800">
//           {isFirstSession ? 'Initial Counseling Session' : 'Follow-up Counseling Session'}
//         </h1>
//         <div className="flex items-center gap-2">
//           <div className="bg-blue-50 px-3 py-1 rounded text-blue-700">
//             Session #{sessionData?.record.sessionNumber || '?'}
//           </div>
//           {/* Auto-save status indicator */}
//           {hasUnsavedChanges && (
//             <div className="bg-amber-50 px-3 py-1 rounded text-amber-700 flex items-center gap-1">
//               <Save className="h-4 w-4 animate-pulse" />
//               <span>Auto-saving...</span>
//             </div>
//           )}

//           {lastAutoSave && !hasUnsavedChanges && (
//             <div className="bg-green-50 px-3 py-1 rounded text-green-700 flex items-center gap-1">
//               <CheckCircle className="h-4 w-4" />
//               <span>Saved {new Date(lastAutoSave).toLocaleTimeString()}</span>
//             </div>
//           )}

//           {sessionData?.status === "completed" && (
//             <div className="bg-green-50 px-3 py-1 rounded text-green-700 flex items-center gap-1">
//               <CheckCircle className="h-4 w-4" />
//               <span>Completed</span>
//             </div>
//           )}
//         </div>
//       </div>

//       {successMessage && (
//         <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
//           <div className="flex">
//             <CheckCircle className="h-5 w-5 text-green-400" />
//             <div className="ml-3">
//               <p className="text-sm text-green-700">{successMessage}</p>
//             </div>
//           </div>
//         </div>
//       )}

//       <SessionProgress
//         activeStep={activeStep}
//         sessionData={sessionData}
//         isStepComplete={isStepComplete}
//         isTerminating={isTerminating}
//         steps={currentSteps}
//         onOpenSafetyForm={openSafetyForm}
//         onOpenTerminationForm={openTerminationForm}
//         // Pass draft-related props to StepContent
//         handleFormChange={handleFormChange}
//         hasUnsavedChanges={hasUnsavedChanges}
//         lastAutoSave={lastAutoSave}
//       />

//       {error && (
//         <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
//           <div className="flex">
//             <AlertCircle className="h-5 w-5 text-red-400" />
//             <div className="ml-3">
//               <p className="text-sm text-red-700">{error}</p>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="bg-white rounded-lg shadow p-2">
//         <StepContent
//           activeStep={activeStep}
//           sessionData={sessionData}
//           onSubmit={handleFormSubmit}
//           isNewClient={isFirstSession}
//           validSteps={currentSteps}
//           actualSessionId={actualSessionId}
//           showSafetyForm={showSafetyForm}
//           showTerminationForm={showTerminationForm}
//           onSafetySubmit={handleSafetyFormSubmit}
//           onTerminationSubmit={handleTerminationFormSubmit}
//         />

//         <div className="px-6 py-4 border-t flex justify-end space-x-3">
//           <button
//             onClick={handleCancel}
//             className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
//             disabled={sessionData?.status === "completed"}
//           >
//             Cancel
//           </button>

//           {!isTerminating && sessionData?.status !== "completed" && (
//             <button
//               onClick={handleSaveAndContinue}
//               disabled={!isStepComplete(activeStep)}
//               className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
//             >
//               {activeStep === "mentalStatus" ? "Complete Session" : "Save & Continue"}
//             </button>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }